import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/contexts/theme.context";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { StoreProvider } from "@/contexts/StoreContext";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { PermissionRoute } from "@/components/auth/PermissionRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { FullPageLoader } from "@/components/ui/full-page-loader";

// Auth Pages
import { LoginPage } from "@/pages/auth/LoginPage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { OnboardingPage } from "@/pages/auth/OnboardingPage";
import { ShareStorePage } from "@/pages/auth/ShareStorePage";

// Dashboard Pages
import { DashboardPage } from "@/pages/dashboard/DashboardPage";
import { OrdersPage } from "@/pages/dashboard/OrdersPage";
import { OrderDetailsPage } from "@/pages/dashboard/OrderDetailsPage";
import { CustomersPage } from "@/pages/dashboard/CustomersPage";
import { CustomerDetailsPage } from "@/pages/dashboard/CustomerDetailsPage";
import { ProductsPage } from "@/pages/dashboard/ProductsPage";
import { InventoryPage } from "@/pages/dashboard/InventoryPage";
import { PosPage } from "@/pages/dashboard/PosPage";
import { ProductDetailsPage } from "@/pages/dashboard/ProductDetailsPage";
import { VariantManager } from "@/components/dashboard/products/VariantManager";
import { CategoryManagementPage } from "@/pages/dashboard/CategoryManagementPage";
import { AnalyticsPage } from "@/pages/dashboard/AnalyticsPage";
import { SalesLinkBuilderPage } from "@/pages/dashboard/SalesLinkBuilderPage";
import { SettingsPage } from "@/pages/dashboard/SettingsPage";
import { BulkUploadPage } from "@/pages/dashboard/BulkUploadPage";
import { BookingSlotsPage } from "@/pages/dashboard/BookingSlotsPage";
import { BookingsPage } from "@/pages/dashboard/BookingsPage";
import { BookingDetailsPage } from "@/pages/dashboard/BookingDetailsPage";
import { InvoicesPage } from "@/pages/dashboard/InvoicesPage";
import { CreateInvoicePage } from "@/pages/dashboard/CreateInvoicePage";
import { InvoiceViewPage } from "@/pages/dashboard/InvoiceViewPage";
import { InvoicePaymentsPage } from "@/pages/dashboard/InvoicePaymentsPage";
import { PublicInvoiceViewPage } from "@/pages/PublicInvoiceViewPage";
import { CreateBookingSlotForm } from "@/components/dashboard/booking-slots/CreateBookingSlotForm";
import { EditBookingSlotForm } from "@/components/dashboard/booking-slots/EditBookingSlotForm";

// Marketing Pages
import { MarketingLayout } from "@/layouts/MarketingLayout";
import { HomePage } from "@/pages/HomePage";
import { FeaturesPage } from "@/pages/FeaturesPage";
import { PricingPage } from "@/pages/PricingPage";
import { CompanyPage } from "@/pages/CompanyPage";
import { ContactPage } from "@/pages/ContactPage";
import { CareersPage } from "@/pages/CareersPage";
import { TeamPage } from "@/pages/TeamPage";
import { CompliancePage } from "@/pages/CompliancePage";
import { SolutionsIndexPage } from "@/pages/solutions/SolutionsIndexPage";
import { SolutionDetailPage } from "@/pages/solutions/SolutionDetailPage";
import { ProductIndexPage } from "@/pages/product/ProductIndexPage";
import { ProductCategoryPage } from "@/pages/product/ProductCategoryPage";
import { TermsPage } from "@/pages/legal/TermsPage";
import { PrivacyPage } from "@/pages/legal/PrivacyPage";
import { MerchantAgreementPage } from "@/pages/legal/MerchantAgreementPage";

// Admin Pages
import { PlatformAnalyticsPage } from "@/pages/admin/PlatformAnalyticsPage";
import { UsersManagementPage } from "@/pages/admin/UsersManagementPage";
import { MarketingPage } from "@/pages/admin/MarketingPage";
import { AllStoresPage } from "@/pages/admin/AllStoresPage";
import { StoreDetailsPage } from "@/pages/admin/StoreDetailsPage";
import { UserDetailsPage } from "@/pages/admin/UserDetailsPage";
import { EmailAutomationPage } from "@/pages/admin/EmailAutomationPage";
import { OnboardingAdminPage } from "@/pages/admin/OnboardingAdminPage";

// Store Pages
import { StorefrontPage } from "@/pages/StorefrontPage";
import { ProductDetailPage } from "@/pages/ProductDetailPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { EnhancedCheckoutPage } from "@/pages/EnhancedCheckoutPage";
import { OrderConfirmationPage } from "@/pages/OrderConfirmationPage";
import { InvoicePage } from "@/pages/InvoicePage";
import { BookingDetailPage } from "@/pages/BookingDetailPage";
import { BookingCheckoutPage } from "@/pages/BookingCheckoutPage";
import { BookingConfirmationPage } from "@/pages/BookingConfirmationPage";
import { CustomerAuthProvider } from "@/contexts/CustomerAuthContext";
import { EnhancedCartProvider } from "@/contexts/EnhancedCartContext";
import { useParams } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { HelmetProvider } from 'react-helmet-async';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
    },
  },
});

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    currentStore,
    stores,
    user,
    isLoadingStores,
  } = useAuth();

  if (isLoading) {
    return <FullPageLoader text="Signing you in" />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" />;
  }

  // Check if onboarding is needed
  if (
    user.onboardingStatus === "pending" &&
    location.pathname !== "/onboarding"
  ) {
    return <Navigate to="/onboarding" />;
  }

  // If user has stores but none is selected (and not still loading stores)
  if (stores.length > 0 && !currentStore && !isLoadingStores) {
    return <FullPageLoader text="Opening your store" />;
  }

  // If user has no stores, redirect to onboarding to create one
  if (
    stores.length === 0 &&
    !isLoadingStores &&
    user?.onboardingStatus === "completed"
  ) {
    return <Navigate to="/onboarding" />;
  }

  return <>{children}</>;
}

function OnboardingProtected({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <FullPageLoader text="Signing you in" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Managers should never see onboarding, redirect them to dashboard
  if (user?.role === "manager" || user?.onboardingStatus === "completed") {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
}

function SubdomainRedirect() {
  if (typeof window !== "undefined") {
    const { hostname, pathname, search, hash, protocol } = window.location;
    const rootDomain = "sqale.shop";

    if (hostname.endsWith(`.${rootDomain}`)) {
      const subdomain = hostname.slice(0, -(`.${rootDomain}`.length));

      if (subdomain && subdomain !== "app") {
        const target = `${protocol}//${rootDomain}/${subdomain}${pathname}${search}${hash}`;
        window.location.replace(target);
        return null;
      }
    }
  }

  return null;
}

function BillingRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: "/dashboard/settings",
        search: location.search,
        hash: "#billing",
      }}
      replace
    />
  );
}

function App() {
  function CheckoutProviders({ children }: { children: React.ReactNode }) {
    const { storeUrl } = useParams<{ storeUrl: string }>();
    return (
      <CustomerAuthProvider storeUrl={storeUrl || ""}>
        <EnhancedCartProvider storeUrl={storeUrl}>
          {children}
        </EnhancedCartProvider>
      </CustomerAuthProvider>
    );
  }

  return (
    <BrowserRouter>
      <SubdomainRedirect />
      <Analytics />
      <ScrollToTop />
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <StoreProvider>
              <SettingsProvider>
                <HelmetProvider>
                  <Toaster
                    position="top-center"
                    closeButton
                    gap={8}
                    toastOptions={{
                      classNames: {
                        toast:
                          "!rounded-2xl !border !border-border !bg-popover !px-4 !py-3.5 !font-sans !text-popover-foreground !shadow-[0_16px_40px_-16px_rgba(0,0,0,0.22)]",
                        title: "!text-[13px] !font-semibold !text-foreground",
                        description: "!text-xs !text-muted-foreground",
                        success: "[&_[data-icon]]:!text-emerald-600",
                        error: "[&_[data-icon]]:!text-red-600",
                        warning: "[&_[data-icon]]:!text-amber-600",
                        info: "[&_[data-icon]]:!text-primary",
                        closeButton:
                          "!border-border !bg-card !text-muted-foreground hover:!text-foreground",
                      },
                    }}
                  />
                  <Routes>
                  {/* Marketing site */}
                  <Route path="/" element={<MarketingLayout />}>
                    <Route index element={<HomePage />} />
                    <Route path="features" element={<FeaturesPage />} />
                    <Route path="product" element={<ProductIndexPage />} />
                    <Route path="product/:slug" element={<ProductCategoryPage />} />
                    <Route path="pricing" element={<PricingPage />} />
                    <Route path="company" element={<CompanyPage />} />
                    <Route path="contact" element={<ContactPage />} />
                    <Route path="solutions" element={<SolutionsIndexPage />} />
                    <Route path="solutions/:slug" element={<SolutionDetailPage />} />
                    <Route path="careers" element={<CareersPage />} />
                    <Route path="team" element={<TeamPage />} />
                    <Route path="compliance" element={<CompliancePage />} />
                    <Route path="terms" element={<TermsPage />} />
                    <Route path="privacy" element={<PrivacyPage />} />
                    <Route path="merchant-agreement" element={<MerchantAgreementPage />} />
                  </Route>
                  
                  {/* Store Page (standalone route outside layouts) */}
                  <Route path="/:storeUrl" element={<StorefrontPage />} />
                  <Route
                    path="/:storeUrl/product/:productId"
                    element={
                      <CheckoutProviders>
                        <ProductDetailPage />
                      </CheckoutProviders>
                    }
                  />
                  <Route
                    path="/:storeUrl/checkout"
                    element={
                      <CheckoutProviders>
                        <EnhancedCheckoutPage />
                      </CheckoutProviders>
                    }
                  />
                  <Route
                    path="/:storeUrl/order-confirmation/:orderId"
                    element={<OrderConfirmationPage />}
                  />
                  <Route
                    path="/:storeUrl/booking/:slotId"
                    element={<BookingDetailPage />}
                  />
                  <Route
                    path="/:storeUrl/booking/:slotId/checkout"
                    element={<BookingCheckoutPage />}
                  />
                  <Route
                    path="/:storeUrl/booking-confirmation/:bookingId"
                    element={<BookingConfirmationPage />}
                  />
                  <Route
                    path="/invoice/:orderId/:token"
                    element={<InvoicePage />}
                  />
                  {/* Public invoice view (from email links) */}
                  <Route
                    path="/invoices/public/:id"
                    element={<PublicInvoiceViewPage />}
                  />

                  {/* Auth Pages */}
                  <Route path="/login" element={<LoginPage />}  />
                  <Route path="/register" element={<RegisterPage />} />
                  
                  <Route
                    path="/onboarding"
                    element={
                      <OnboardingProtected>
                        <OnboardingPage />
                      </OnboardingProtected>
                    }
                  />
                  <Route
                    path="/auth/share-store"
                    element={<ShareStorePage />}
                  />

                  {/* Dashboard Pages */}
                  <Route
                    path="/dashboard"
                    element={
                      <PrivateRoute>
                        <DashboardLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<DashboardPage />} />
                    <Route path="products" element={<PermissionRoute permission="products"><ProductsPage /></PermissionRoute>} />
                    <Route path="inventory" element={<PermissionRoute permission="inventory"><InventoryPage /></PermissionRoute>} />
                    <Route path="pos" element={<PermissionRoute permission="pos"><PosPage /></PermissionRoute>} />
                    <Route
                      path="products/new"
                      element={<PermissionRoute permission="products"><ProductDetailsPage /></PermissionRoute>}
                    />
                    <Route
                      path="products/:productId"
                      element={<PermissionRoute permission="products"><ProductDetailsPage /></PermissionRoute>}
                    />
                    <Route
                      path="products/:productId/variants"
                      element={<PermissionRoute permission="products"><VariantManager /></PermissionRoute>}
                    />
                    <Route
                      path="categories"
                      element={<PermissionRoute permission="products"><CategoryManagementPage /></PermissionRoute>}
                    />
                    <Route path="orders" element={<PermissionRoute permission="orders"><OrdersPage /></PermissionRoute>} />
                    <Route
                      path="orders/:orderId"
                      element={<PermissionRoute permission="orders"><OrderDetailsPage /></PermissionRoute>}
                    />
                    <Route path="customers" element={<PermissionRoute permission="customers"><CustomersPage /></PermissionRoute>} />
                    <Route
                      path="customers/:customerId"
                      element={<PermissionRoute permission="customers"><CustomerDetailsPage /></PermissionRoute>}
                    />
                    <Route path="analytics" element={<PermissionRoute permission="analytics"><AnalyticsPage /></PermissionRoute>} />
                    <Route path="sales-link" element={<PermissionRoute permission="settings"><SalesLinkBuilderPage /></PermissionRoute>} />
                    <Route path="billing" element={<PermissionRoute ownerOnly><BillingRedirect /></PermissionRoute>} />
                    <Route path="bulk-upload" element={<PermissionRoute permission="products" ownerOnly><BulkUploadPage /></PermissionRoute>} />
                    <Route path="booking-slots" element={<PermissionRoute permission="bookings"><BookingSlotsPage /></PermissionRoute>} />
                    <Route
                      path="booking-slots/new"
                      element={<PermissionRoute permission="bookings"><CreateBookingSlotForm /></PermissionRoute>}
                    />
                    <Route
                      path="booking-slots/:id/edit"
                      element={<PermissionRoute permission="bookings"><EditBookingSlotForm /></PermissionRoute>}
                    />
                    <Route path="bookings" element={<PermissionRoute permission="bookings"><BookingsPage /></PermissionRoute>} />
                    <Route
                      path="bookings/:id"
                      element={<PermissionRoute permission="bookings"><BookingDetailsPage /></PermissionRoute>}
                    />
                    <Route path="invoices" element={<PermissionRoute permission="invoices"><InvoicesPage /></PermissionRoute>} />
                    <Route path="invoices/new" element={<PermissionRoute permission="invoices"><CreateInvoicePage /></PermissionRoute>} />
                    <Route path="invoices/payments" element={<PermissionRoute permission="invoices"><InvoicePaymentsPage /></PermissionRoute>} />
                    <Route path="invoices/:id" element={<PermissionRoute permission="invoices"><InvoiceViewPage /></PermissionRoute>} />
                    <Route path="invoices/:id/edit" element={<PermissionRoute permission="invoices"><CreateInvoicePage /></PermissionRoute>} />
                    <Route path="settings/*" element={<SettingsPage />} />
                  </Route>

                  {/* Admin Pages */}
                  <Route
                    path="/admin/platform-analytics"
                    element={
                      <PrivateRoute>
                        <DashboardLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<PlatformAnalyticsPage />} />
                  </Route>
                  <Route
                    path="/admin/onboarding"
                    element={
                      <PrivateRoute>
                        <DashboardLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<OnboardingAdminPage />} />
                  </Route>
                  <Route
                    path="/admin/users"
                    element={
                      <PrivateRoute>
                        <DashboardLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<UsersManagementPage />} />
                  </Route>
                  <Route
                    path="/admin/marketing"
                    element={
                      <PrivateRoute>
                        <DashboardLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<MarketingPage />} />
                  </Route>
                  <Route
                    path="/admin/email-automation"
                    element={
                      <PrivateRoute>
                        <DashboardLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<EmailAutomationPage />} />
                  </Route>
                  <Route
                    path="/admin/stores"
                    element={
                      <PrivateRoute>
                        <DashboardLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<AllStoresPage />} />
                  </Route>
                  <Route
                    path="/admin/stores/:storeId"
                    element={
                      <PrivateRoute>
                        <DashboardLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<StoreDetailsPage />} />
                  </Route>
                  <Route
                    path="/admin/users/:userId"
                    element={
                      <PrivateRoute>
                        <DashboardLayout />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<UserDetailsPage />} />
                  </Route>

                  {/* Checkout Page */}
                  <Route path="/checkout" element={<CheckoutPage />} />

                  {/* Order Confirmation Page */}
                  <Route
                    path="/order-confirmation/:orderId"
                    element={<OrderConfirmationPage />}
                  />
                  <Route
                    path="/invoice/:orderId/:token"
                    element={<InvoicePage />}
                  />
                  </Routes>
                </HelmetProvider>
              </SettingsProvider>
            </StoreProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
